import { Blueprint } from '@/components/builder/base/blueprints/Blueprint';
import { ValidatableBlueprint } from '@/components/builder/base/blueprints/ValidatableBlueprint';
import { HasWidth } from '@/components/builder/base/traits/HasWidth';
import { ValidationErrors } from '@/components/builder/base/types/ValidationErrors';
import { FormBuilderContract } from '@/components/builder/form';
import { BlueprintDefinition } from '@/components/builder/form/blueprints/BlueprintDefinition';
import { CustomErrorBlueprint } from '@/components/builder/form/blueprints/CustomErrorBlueprint';
import { ReadonlyBlueprint } from '@/components/builder/form/blueprints/ReadonlyBlueprint';
import { RequiredBlueprint } from '@/components/builder/form/blueprints/RequiredBlueprint';
import { VisibleBlueprint } from '@/components/builder/form/blueprints/VisibleBlueprint';
import { Entry, entry } from '@/components/builder/form/entries/Entry';
import { ValidEntry } from '@/components/builder/form/entries/ValidEntry';
import { AlwaysChoice } from '@/components/builder/form/enums/AlwaysChoice';
import { InternallyChoice } from '@/components/builder/form/enums/InternallyChoice';
import { NeverChoice } from '@/components/builder/form/enums/NeverChoice';
import { WhenChoice } from '@/components/builder/form/enums/WhenChoice';
import { EntryFactory } from '@/components/builder/form/traits/EntryFactory';
import { HasHelp } from '@/components/builder/form/traits/HasHelp';
import { HasLabel } from '@/components/builder/form/traits/HasLabel';
import { HasPlaceholder } from '@/components/builder/form/traits/HasPlaceholder';
import { HasClientSideTriggers, HasServerSideTriggers, TriggerModel, TriggerMoments } from '@/components/builder/form/traits/HasTriggers';
import { ChoiceOption } from '@/components/builder/form/types/ChoiceOption';
import { ProcessCallback } from '@/components/builder/form/types/ProcessCallback';

export const Definition: BlueprintDefinition = {
    type: 'logito-choice',
    name: '[[[Wybór]]]',
    icon: 'fa-list',
    group: 'primary',
    position: 2,
};

export enum LogitoChoiceFieldTypes {
    Dropdown = 'Dropdown',
    Radio = 'Radio',
    Checkbox = 'Checkbox',
}

export class LogitoChoiceEntry extends ValidEntry<string[]>
{
    public type: string = Definition.type;
    public data: string[] = null;
    public index?: number = null;

    public constructor(data: any = null, index: number = null)
    {
        super();

        if (data !== null)
        {
            this.data = data;
        }

        this.index = index;
    }

    public async collect(
        blueprint: LogitoChoiceContract,
        form: FormBuilderContract,
        preprocess: ProcessCallback
    ): Promise<Entry>
    {
        await preprocess(blueprint, this, form.blueprintId, form.entryId);

        const data = this.getValues(blueprint, form);

        return entry(data);
    }

    public validate(blueprint: LogitoChoiceContract, form: FormBuilderContract): boolean
    {
        this.errors = {};

        const data = this.getValues(blueprint, form);

        if (!form.expressions.readonly(blueprint, true, this.index) && form.expressions.visible(blueprint, true, [], this.index))
        {
            if (form.expressions.required(blueprint, this.index) && (data == null || data.length == 0))
            {
                this.errors.value = [`[[[Pole "%0" jest wymagane.|||${form.localization.translate(blueprint.label)}]]]`];
            }
            else if (form.expressions.customError(blueprint))
            {
                this.errors.custom = [form.expressions.customErrorMessage(blueprint, form)];
            }
        }

        return this.valid();
    }

    private getValues(blueprint: LogitoChoiceContract, form: FormBuilderContract): string[]
    {
        const readonly = form.expressions.readonly(blueprint, true, this.index);
        const defaultValue = blueprint.defaultValue
            ? blueprint.options.filter((p) => p.value === form.expressions.executeExpression(blueprint.defaultValue, this.index)).map((p) => p.key)
            : blueprint.options.filter((p) => p.selected).map((p) => p.key);

        return this.data === null || (readonly && defaultValue.length) ? defaultValue : this.data;
    }
}

export const instanceOfLogitoChoiceEntry = (object: any): object is LogitoChoiceEntry =>
{
    return object && 'type' in object && object.type === Definition.type;
};

export interface LogitoChoiceContract
    extends Blueprint,
    VisibleBlueprint,
    ReadonlyBlueprint,
    RequiredBlueprint,
    CustomErrorBlueprint,
    HasLabel,
    HasPlaceholder,
    HasHelp,
    HasWidth,
    HasServerSideTriggers,
    HasClientSideTriggers {
    fieldType: LogitoChoiceFieldTypes;
    options: ChoiceOption[];
    defaultValue: string;
    layout: number;
}

export class LogitoChoiceType implements LogitoChoiceContract, ValidatableBlueprint, EntryFactory<LogitoChoiceEntry>
{
    public id: string;
    public type: string;
    public name: string;
    public label: Record<string, string>;
    public showLabel: boolean;
    public placeholder: Record<string, string>;
    public help: Record<string, string>;
    public width: number;
    public minWidth: number;
    public fieldType: LogitoChoiceFieldTypes;
    public options: ChoiceOption[];
    public defaultValue: string;
    public layout: number;
    public visible: AlwaysChoice | NeverChoice | InternallyChoice | WhenChoice;
    public visibleWhen: string;
    public readonly: AlwaysChoice | NeverChoice | InternallyChoice | WhenChoice;
    public readonlyWhen: string;
    public required: AlwaysChoice | NeverChoice | WhenChoice;
    public requiredWhen: string;
    public customError: NeverChoice | WhenChoice;
    public customErrorWhen: string;
    public customErrorMessage: Record<string, string>;
    public errors: ValidationErrors;
    public showFilter: boolean;
    public serverSideTriggerMoments: TriggerMoments[] = [ TriggerMoments.onChange ];
    public serverSideTriggers: TriggerModel[];
    public clientSideTriggerMoments: TriggerMoments[] = [ TriggerMoments.onChange ];
    public clientSideTriggers: TriggerModel[];

    public constructor(id: string, name: string)
    {
        this.id = id;
        this.type = Definition.type;
        this.name = name;
        this.label = { 'pl-PL': 'Wybór' };
        this.showLabel = true;
        this.placeholder = {};
        this.options = [
            { value: '1', text: { 'pl-PL': 'Primus...' }, selected: false, key: '1' },
            { value: '2', text: { 'pl-PL': 'Secundus...' }, selected: false, key: '2' },
            { value: '3', text: { 'pl-PL': 'Tetrium...' }, selected: false, key: '3' },
        ];
        this.layout = 0;
        this.defaultValue = '';
        this.help = {};
        this.fieldType = LogitoChoiceFieldTypes.Dropdown;
        this.width = 0;
        this.minWidth = 1;
        this.customError = NeverChoice.Never;
        this.customErrorWhen = null;
        this.customErrorMessage = {};
        this.readonly = NeverChoice.Never;
        this.readonlyWhen = null;
        this.required = NeverChoice.Never;
        this.requiredWhen = null;
        this.visible = AlwaysChoice.Always;
        this.visibleWhen = null;
        this.errors = {};
        this.showFilter = false;
        this.serverSideTriggers = [];
    }

    public setDefaultWidth(width: number): void
    {
        this.width = Math.min(3, Math.max(this.minWidth, width));
    }

    public createEntry(data: any): LogitoChoiceEntry
    {
        return new LogitoChoiceEntry(data);
    }

    public updateEntry(data: any, entry: LogitoChoiceEntry): LogitoChoiceEntry
    {
        return new LogitoChoiceEntry(data);
    }

    public validate(): Record<string, ValidationErrors>
    {
        this.errors = {};

        return {
            [this.name]: this.errors,
        };
    }
}
